import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BackPrintConfig } from "../../../types/props";
import { auth, teams as teamsService, kits as kitsService } from "../../../services/supabase";
import { api, PricingTier, FALLBACK_PRICING_TIERS, calculateUnitPrice } from "../../../services/api";
import { useLoadingMessages } from "../useLoadingMessages";

const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

interface GuestCheckoutStepProps {
  teamName: string;
  teamSummary: string;
  teamLogoDataUrl: string;
  kitType: string;
  teamwearColor: string;
  emblemColor: string;
  manualEmblemColor: boolean;
  teamLogo: string;
  backPrint: BackPrintConfig;
  showBackImage: boolean;
  backImageScale: number;
  designName: string;
  previewFrontDataUrl: string;
  previewBackDataUrl: string;
  onBack: () => void;
}

const GuestCheckoutStep: React.FC<GuestCheckoutStepProps> = ({
  teamName,
  teamSummary,
  teamLogoDataUrl,
  kitType,
  teamwearColor,
  backPrint,
  showBackImage,
  backImageScale,
  designName,
  previewFrontDataUrl,
  previewBackDataUrl,
  onBack,
}) => {
  const navigate = useNavigate();
  const [quantities, setQuantities] = useState<Record<string, number>>(
    Object.fromEntries(SIZES.map(s => [s, 0]))
  );
  const [address, setAddress] = useState({ line1: "", line2: "", city: "", postcode: "" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [saving, setSaving] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState("");
  const [tiers, setTiers] = useState<PricingTier[]>(FALLBACK_PRICING_TIERS);

  useEffect(() => {
    api.getPricing()
      .then(({ tiers }) => setTiers(tiers))
      .catch(() => { /* keep fallback tiers */ });
  }, []);

  const { message: saveMsg, animKey: saveKey } = useLoadingMessages(
    ["Creating your account…", "Saving your team…", "Storing your kit design…", "All done…"],
    saving,
    2000
  );
  const { message: checkoutMsg, animKey: checkoutKey } = useLoadingMessages(
    ["Creating your account…", "Saving your team…", "Storing your kit design…", "Setting up payment…", "Almost there…"],
    checkingOut,
    2200
  );

  const updateQty = (size: string, val: number) =>
    setQuantities(prev => ({ ...prev, [size]: Math.max(0, val) }));

  const totalItems = Object.values(quantities).reduce((s, q) => s + q, 0);
  const unitPrice = calculateUnitPrice(totalItems, tiers);
  const total = totalItems * unitPrice;

  const validateAccountFields = (): string | null => {
    if (!username.trim()) return "Username is required.";
    if (!email.trim()) return "Email is required.";
    if (password.length < 6) return "Password must be at least 6 characters.";
    return null;
  };

  const saveTeamAndKit = async () => {
    const { data: newUser, session, error: authError, alreadyRegistered, alreadyConfirmed } = await auth.signUp(email.trim(), password, { username: username.trim() });
    if (authError) throw authError;
    if (!newUser) throw new Error("Account creation failed. Please try again.");
    if (alreadyConfirmed) throw new Error("An account with this email already exists. Please log in to continue your order.");
    if (!session) {
      throw new Error(alreadyRegistered
        ? "An account with this email already exists but isn't confirmed yet — we've sent a new confirmation email. Check your inbox, then log in to complete your order."
        : "Please check your email to verify your account, then log in to complete your order.");
    }

    let flagged = false;
    try {
      const [nameCheck, summaryCheck] = await Promise.all([
        api.moderateText(teamName),
        teamSummary ? api.moderateText(teamSummary) : Promise.resolve({ flagged: false }),
      ]);
      flagged = nameCheck.flagged || summaryCheck.flagged;
    } catch (moderationErr) {
      console.error("Failed to moderate team content:", moderationErr);
    }
    if (flagged) {
      throw new Error("Your team name or description can't contain explicit, hateful, or derogatory content. Please go back and edit it.");
    }

    const teamId = crypto.randomUUID();
    const kitId = crypto.randomUUID();

    let logoPublicUrl = "";
    let logoThumbUrl = "";
    if (teamLogoDataUrl) {
      const { data: logoData, error: logoError } = await teamsService.uploadLogoBase64(teamId, teamLogoDataUrl);
      if (logoError) throw logoError;
      logoPublicUrl = logoData!.logoUrl;
      logoThumbUrl = logoData!.thumbUrl;
    }

    const { data: frontUrl, error: frontErr } = await kitsService.uploadPreviewImage(kitId, previewFrontDataUrl, 'front');
    if (frontErr) throw frontErr;
    const { data: backUrl, error: backErr } = await kitsService.uploadPreviewImage(kitId, previewBackDataUrl, 'back');
    if (backErr) throw backErr;

    const { data: teamData, error: teamError } = await teamsService.create({
      team_name: teamName,
      summary: teamSummary,
      logo_url: logoPublicUrl || null,
      logo_thumb_url: logoThumbUrl || null,
      user_email: email.trim(),
      created_by: newUser.id,
    });
    if (teamError) throw teamError;

    const { error: kitError } = await kitsService.create({
      kit_id: kitId,
      team_id: teamData.id,
      design_name: designName || "Untitled Design",
      created_by_email: email.trim(),
      status: "draft",
      kit_type: kitType,
      teamwear_color: teamwearColor,
      emblem_color: null,
      front_image_url: null,
      front_image_show: true,
      front_image_scale: 100,
      back_image_url: null,
      back_image_show: showBackImage,
      back_image_scale: backImageScale,
      back_print_enabled: backPrint.enabled,
      back_print_text: backPrint.text || null,
      back_print_font: backPrint.font || null,
      back_print_scale: backPrint.scale,
      back_print_position: backPrint.position,
      preview_front_url: frontUrl,
      preview_back_url: backUrl,
      team_name: teamName,
    });
    if (kitError) throw kitError;

    return { kitId, teamId: teamData.id, frontUrl, backUrl, accessToken: session.access_token };
  };

  const handleSave = async () => {
    setError("");
    const fieldError = validateAccountFields();
    if (fieldError) { setError(fieldError); return; }

    setSaving(true);
    try {
      await saveTeamAndKit();
      navigate("/account");
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.");
      setSaving(false);
    }
  };

  const handleCheckout = async () => {
    setError("");
    const fieldError = validateAccountFields();
    if (fieldError) { setError(fieldError); return; }
    if (totalItems === 0) { setError("Please select at least one size."); return; }
    if (!address.line1.trim() || !address.city.trim() || !address.postcode.trim()) {
      setError("Please complete your delivery address.");
      return;
    }

    setCheckingOut(true);
    try {
      const { kitId, teamId, frontUrl, backUrl, accessToken } = await saveTeamAndKit();

      const kit_snapshot = {
        design_name: designName || "Untitled Design",
        kit_type: kitType,
        teamwear_color: teamwearColor,
        emblem_color: null,
        preview_front_url: frontUrl || "",
        preview_back_url: backUrl || "",
        front_image_url: "",
        back_image_url: "",
        back_print_text: backPrint.text || "",
        back_print_enabled: backPrint.enabled ? "True" : "False",
        back_print_position: backPrint.position || 50,
        team_name: teamName,
      };

      const { checkout_url } = await api.createCheckoutSession({
        kit_id: kitId,
        team_id: teamId,
        quantities,
        kit_snapshot,
        customer_name: username.trim(),
        team_name: teamName,
        design_name: designName || "Untitled Design",
        delivery_address_line1: address.line1.trim(),
        delivery_address_line2: address.line2.trim(),
        delivery_city: address.city.trim(),
        delivery_postcode: address.postcode.trim().toUpperCase(),
      }, accessToken);
      window.location.href = checkout_url;
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.");
      setCheckingOut(false);
    }
  };

  const busy = saving || checkingOut;

  return (
    <div className="bk-checkout">
      <div className="bk-checkout-eyebrow">[ 03 / Checkout ]</div>
      <h1 className="bk-checkout-title">Almost there.</h1>
      <p className="bk-checkout-subtitle">Create your account to save your kit and place your order.</p>

      {error && <div className="bk-error">{error}</div>}

      <div className="bk-checkout-form">
        <div className="bk-checkout-cols">

          {/* LEFT — Kit summary + sizes + delivery */}
          <div className="bk-left">

            {/* Kit preview */}
            {(previewFrontDataUrl || previewBackDataUrl) && (
              <div className="bk-card">
                <div className="bk-card-title">Your Kit — {designName || "Untitled Design"}</div>
                <div className="bk-preview-pair">
                  {previewFrontDataUrl && (
                    <div className="bk-preview-block">
                      <div className="bk-preview-label">Front</div>
                      <img src={previewFrontDataUrl} alt="Kit front" className="bk-preview-img" />
                    </div>
                  )}
                  {previewBackDataUrl && (
                    <div className="bk-preview-block">
                      <div className="bk-preview-label">Back</div>
                      <img src={previewBackDataUrl} alt="Kit back" className="bk-preview-img" />
                    </div>
                  )}
                </div>
                {backPrint.enabled && backPrint.text && (
                  <div className="bk-spec-row">
                    <span className="bk-spec-label">Back print</span>
                    <span className="bk-spec-value">"{backPrint.text}"</span>
                  </div>
                )}
              </div>
            )}

            {/* Size selector */}
            <div className="bk-card">
              <div className="bk-card-title">Select Quantities</div>
              <p className="bk-account-desc" style={{ marginBottom: "1rem" }}>
                Only needed if you're ordering now — you can also save your kit and order later.
              </p>
              <div className="bk-sizes">
                {SIZES.map(size => (
                  <div key={size} className="bk-size-row">
                    <span className="bk-size-label">{size}</span>
                    <div className="bk-qty">
                      <button type="button" className="bk-qty-btn" onClick={() => updateQty(size, quantities[size] - 1)} disabled={busy}>−</button>
                      <span className="bk-qty-val">{quantities[size]}</span>
                      <button type="button" className="bk-qty-btn" onClick={() => updateQty(size, quantities[size] + 1)} disabled={busy}>+</button>
                    </div>
                  </div>
                ))}
              </div>
              {totalItems > 0 && (
                <div className="bk-summary">
                  <div className="bk-summary-row">
                    <span>{totalItems} item{totalItems !== 1 ? "s" : ""} @ £{unitPrice.toFixed(2)} each (inc. VAT)</span>
                    <span>£{total.toFixed(2)}</span>
                  </div>
                  <div className="bk-summary-row bk-summary-total">
                    <span>Total (inc. VAT)</span>
                    <span>£{total.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Delivery address */}
            <div className="bk-card">
              <div className="bk-card-title">Delivery Address</div>
              <p className="bk-account-desc" style={{ marginBottom: "1rem" }}>Required for placing an order.</p>
              <div className="bk-address-form">
                <div className="bk-field bk-field-full">
                  <label className="bk-field-label">Address line 1</label>
                  <input type="text" className="bk-input" placeholder="Street address"
                    value={address.line1} onChange={e => setAddress(p => ({ ...p, line1: e.target.value }))} disabled={busy} />
                </div>
                <div className="bk-field bk-field-full">
                  <label className="bk-field-label">Address line 2</label>
                  <input type="text" className="bk-input" placeholder="Apartment, suite, unit (optional)"
                    value={address.line2} onChange={e => setAddress(p => ({ ...p, line2: e.target.value }))} disabled={busy} />
                </div>
                <div className="bk-field">
                  <label className="bk-field-label">City</label>
                  <input type="text" className="bk-input" placeholder="City"
                    value={address.city} onChange={e => setAddress(p => ({ ...p, city: e.target.value }))} disabled={busy} />
                </div>
                <div className="bk-field">
                  <label className="bk-field-label">Postcode</label>
                  <input type="text" className="bk-input" placeholder="e.g. SW1A 1AA"
                    value={address.postcode} onChange={e => setAddress(p => ({ ...p, postcode: e.target.value }))} disabled={busy} />
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Account creation */}
          <div className="bk-right">
            <div className="bk-card bk-account-card">
              <div className="bk-card-title">Save Your Kit</div>
              <p className="bk-account-desc">
                Create a free account to save your team and kit design. You can order later, or place your order now.
              </p>

              <div className="bk-field">
                <label className="bk-field-label">Username <span className="bk-required">*</span></label>
                <input type="text" className="bk-input" placeholder="user_name"
                  value={username} onChange={e => setUsername(e.target.value)} autoComplete="username" disabled={busy} />
              </div>
              <div className="bk-field">
                <label className="bk-field-label">Email <span className="bk-required">*</span></label>
                <input type="email" className="bk-input" placeholder="you@example.com"
                  value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" disabled={busy} />
              </div>
              <div className="bk-field">
                <label className="bk-field-label">Password <span className="bk-required">*</span></label>
                <input type="password" className="bk-input" placeholder="Min. 6 characters"
                  value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" disabled={busy} />
              </div>

              <div className="bk-checkout-actions">
                <button type="button" className="bk-btn-text" onClick={onBack} disabled={busy}>
                  ← Back
                </button>
                <button type="button" className="bk-btn-ghost" onClick={handleSave} disabled={busy}>
                  {saving ? <span key={saveKey} className="bk-loading-msg">{saveMsg}</span> : "Save Kit"}
                </button>
                <button type="button" className="bk-btn" onClick={handleCheckout} disabled={busy || totalItems === 0}>
                  {checkingOut ? <span key={checkoutKey} className="bk-loading-msg">{checkoutMsg}</span> : `Pay £${total.toFixed(2)} →`}
                </button>
              </div>

              <div className="bk-stripe-notice">
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                </svg>
                Secure checkout powered by Stripe
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default GuestCheckoutStep;
